/**
 * SubscriptionContext
 *
 * Manages subscription state and provides subscription-related functionality
 * across the application. This context handles:
 * - Active subscription status
 * - Service plan details and limits
 * - Feature access control
 * - User count tracking
 * - Subscription updates and refreshes
 *
 * @module SubscriptionContext
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import SubscriptionsAPI from "../services/SubscriptionsAPI";

// Statuses that, on their own, grant feature access.
const ALWAYS_ACTIVE_STATUSES = new Set(["ACTIVE", "PAST_DUE", "GRACE_PERIOD"]);

// History statuses that indicate the account previously had a real, paid
// subscription that is no longer active (i.e. it has lapsed). Used to tell a
// lapsed account apart from a brand-new account that never subscribed.
// PENDING_PAYMENT is intentionally excluded — it represents a checkout that was
// started but never completed, which should be treated as "never subscribed"
// so onboarding is not blocked.
const LAPSED_HISTORY_STATUSES = new Set(["EXPIRED", "INACTIVE", "CANCELLED"]);

/**
 * Determine whether a subscription payload should grant feature access.
 *
 * This is a defense-in-depth check that mirrors the backend's
 * "active subscription" rules. It exists so that a backend bug or an
 * unexpected payload (e.g. a CANCELLED row leaking through) cannot silently
 * unlock paid features on the client.
 *
 * Rules:
 * - No data → no access.
 * - ACTIVE / PAST_DUE / GRACE_PERIOD → access.
 * - CANCELLED → access ONLY while endDate is set AND in the future.
 * - Anything else (EXPIRED, INACTIVE, PENDING_PAYMENT, unknown) → no access.
 *
 * @param {Object|null|undefined} sub - Subscription payload from the API
 * @returns {boolean}
 */
const isSubscriptionAccessActive = (sub) => {
  if (!sub) return false;

  const status = sub.status;
  if (!status) return false;

  if (ALWAYS_ACTIVE_STATUSES.has(status)) return true;

  if (status === "CANCELLED") {
    const endDateRaw = sub.endDate || sub.accessEndDate;
    if (!endDateRaw) return false;
    const endDate = new Date(endDateRaw);
    if (Number.isNaN(endDate.getTime())) return false;
    // Compare against start-of-today to allow access through the final day.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate >= today;
  }

  return false;
};

// Create the SubscriptionContext
const SubscriptionContext = createContext();

/**
 * Custom hook to use the SubscriptionContext
 * @throws {Error} If used outside of SubscriptionProvider
 * @returns {Object} Subscription context value
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
};

/**
 * SubscriptionProvider component to wrap the app and provide subscription state
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.userUuid - Current user's UUID (optional, for automatic loading)
 */
export const SubscriptionProvider = ({ children, userUuid }) => {
  // =========================================================================
  // STATE MANAGEMENT
  // =========================================================================

  const [subscription, setSubscription] = useState(null);
  const [servicePlan, setServicePlan] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasLapsedSubscription, setHasLapsedSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [error, setError] = useState(null);

  // =========================================================================
  // DEBUG LOGGING
  // =========================================================================

  useEffect(() => {
    console.log(
      "💳 SubscriptionContext: subscription state changed:",
      subscription,
    );
  }, [subscription]);

  useEffect(() => {
    console.log(
      "📋 SubscriptionContext: servicePlan state changed:",
      servicePlan,
    );
  }, [servicePlan]);

  useEffect(() => {
    console.log(
      "✅ SubscriptionContext: hasActiveSubscription changed:",
      hasActiveSubscription,
    );
  }, [hasActiveSubscription]);

  // =========================================================================
  // CORE FUNCTIONS
  // =========================================================================

  /**
   * Fetches subscription data from the API
   * @returns {Promise<Object>} Subscription data
   */
  const fetchSubscriptionData = async () => {
    try {
      console.log("🔄 SubscriptionContext: Fetching subscription data...");
      setIsLoading(true);
      setError(null);

      const response = await SubscriptionsAPI.getCurrentSubscription();

      console.log("📦 SubscriptionContext: API response:", response);

      if (response?.success && response?.data) {
        const subscriptionData = response.data;
        setSubscription(subscriptionData);
        setServicePlan(subscriptionData.servicePlan || null);

        // Defense-in-depth: even if the backend returns a subscription row, do
        // not grant feature access for terminal/inactive statuses or when a
        // CANCELLED subscription's paid access window has ended.
        // The backend should already filter these out, but we double-check here
        // so a single backend regression cannot silently re-open paid features
        // (this guards against the open-ended-access bug we previously hit).
        setHasActiveSubscription(isSubscriptionAccessActive(subscriptionData));

        // A current active subscription was returned, so the account has not
        // lapsed.
        setHasLapsedSubscription(false);

        console.log(
          "✅ SubscriptionContext: Subscription data loaded successfully",
        );
        return subscriptionData;
      } else {
        // No active subscription returned. The /user/current endpoint only
        // returns ACTIVE / PAST_DUE / GRACE_PERIOD / in-window CANCELLED
        // subscriptions, so a lapsed (EXPIRED / INACTIVE / out-of-window
        // CANCELLED) account is indistinguishable here from one that never
        // subscribed. Consult subscription history to tell them apart: a lapsed
        // account has at least one terminal subscription on record, while a
        // brand-new account has none. This keeps new-owner onboarding working
        // while still locking down lapsed accounts.
        setSubscription(null);
        setServicePlan(null);
        setHasActiveSubscription(false);

        try {
          const historyResponse =
            await SubscriptionsAPI.getSubscriptionHistoryByUuid();
          const history = Array.isArray(historyResponse?.data)
            ? historyResponse.data
            : Array.isArray(historyResponse)
              ? historyResponse
              : [];
          const lapsed = history.some((sub) =>
            LAPSED_HISTORY_STATUSES.has(sub?.status),
          );
          setHasLapsedSubscription(lapsed);
          console.log(
            lapsed
              ? "⚠️ SubscriptionContext: Account has a lapsed subscription (no active sub, terminal history present)"
              : "ℹ️ SubscriptionContext: No active subscription and no lapsed history (treated as never-subscribed)",
          );
        } catch (historyErr) {
          // If history cannot be loaded, fail open to "not lapsed" so we never
          // wrongly lock a user out due to a transient history-fetch error.
          console.warn(
            "⚠️ SubscriptionContext: Could not load subscription history to determine lapsed state:",
            historyErr?.message || historyErr,
          );
          setHasLapsedSubscription(false);
        }

        console.log("ℹ️ SubscriptionContext: No active subscription found");
        return null;
      }
    } catch (err) {
      console.error(
        "❌ SubscriptionContext: Error fetching subscription:",
        err,
      );
      setError(err.message || "Failed to fetch subscription");
      setSubscription(null);
      setServicePlan(null);
      setHasActiveSubscription(false);
      setHasLapsedSubscription(false);
      return null;
    } finally {
      setIsLoading(false);
      setHasInitiallyLoaded(true);
    }
  };

  /**
   * Refreshes subscription data
   * Useful after subscription changes (create, update, cancel)
   * @returns {Promise<Object|null>} Subscription data or null
   */
  const refreshSubscription = async () => {
    console.log("🔄 SubscriptionContext: Refreshing subscription data...");
    return await fetchSubscriptionData();
  };

  /**
   * Clears all subscription data
   * Useful on logout or session timeout
   */
  const clearSubscription = () => {
    console.log("🧹 SubscriptionContext: Clearing subscription data...");
    setSubscription(null);
    setServicePlan(null);
    setHasActiveSubscription(false);
    setHasLapsedSubscription(false);
    setHasInitiallyLoaded(false);
    setError(null);
  };

  // =========================================================================
  // FEATURE ACCESS HELPERS
  // =========================================================================

  /**
   * Gets the maximum number of users allowed by the current plan
   * @returns {number} Maximum users allowed (0 if no subscription)
   */
  const getMaxUsers = () => {
    return servicePlan?.maxUsers || 0;
  };

  /**
   * Gets the current plan's display name
   * @returns {string} Plan name or "No Plan"
   */
  const getPlanName = () => {
    return servicePlan?.displayName || servicePlan?.planName || "No Plan";
  };

  /**
   * Gets the plan ID
   * @returns {number|null} Plan ID or null
   */
  const getPlanId = () => {
    return servicePlan?.planId || null;
  };

  /**
   * Checks if a specific feature is available in the current plan
   * @param {string} featureName - Name of the feature to check
   * @returns {boolean} True if feature is available
   */
  const hasFeature = (featureName) => {
    if (!servicePlan?.features) return false;

    // If features is an array
    if (Array.isArray(servicePlan.features)) {
      return servicePlan.features.includes(featureName);
    }

    // If features is an object
    if (typeof servicePlan.features === "object") {
      return !!servicePlan.features[featureName];
    }

    return false;
  };

  /**
   * Whether the current plan supports family sharing (more than 1 user seat).
   * Individual plans (maxUsers === 1) cannot share alerts, scam reports, or training.
   * Unlimited plans (maxUsers === -1) always can.
   *
   * @returns {boolean}
   */
  const canShare = () => {
    if (!hasActiveSubscription) return false;
    const max = getMaxUsers();
    if (max === -1 || max === null) return true; // unlimited
    return max > 1;
  };

  /**
   * Checks if the account can add more users
   * @param {number} currentUserCount - Current number of users in account
   * @returns {boolean} True if more users can be added
   */
  const canAddUsers = (currentUserCount) => {
    if (!hasActiveSubscription) return false;
    const maxUsers = getMaxUsers();
    if (maxUsers === -1 || maxUsers === null) return true; // Unlimited
    return currentUserCount < maxUsers;
  };

  /**
   * Checks if downgrading to a specific plan is allowed
   * @param {number} newPlanMaxUsers - Max users of the new plan
   * @param {number} currentUserCount - Current number of users
   * @returns {boolean} True if downgrade is allowed
   */
  const canDowngradeTo = (newPlanMaxUsers, currentUserCount) => {
    if (newPlanMaxUsers === -1 || newPlanMaxUsers === null) return true; // Unlimited plan
    return currentUserCount <= newPlanMaxUsers;
  };

  /**
   * Computes a discounted price using the current subscription's discount.
   * @param {number} basePrice - Original price before discount
   * @returns {{ discountedPrice: number|null, discountPercent: number }} The discounted price and percent, or null if no discount
   */
  const getDiscountedPrice = (basePrice) => {
    const pct = parseFloat(subscription?.discountPercent) || 0;
    if (pct > 0 && basePrice > 0) {
      return {
        discountedPrice: basePrice * (1 - pct / 100),
        discountPercent: pct,
      };
    }
    return { discountedPrice: null, discountPercent: 0 };
  };

  // =========================================================================
  // COMPUTED PRICE INFO (memoized)
  // =========================================================================

  /**
   * Pre-computed pricing info for the current subscription.
   * Components can use this directly instead of recalculating inline.
   *
   * @type {{ basePrice: number, displayPrice: number, discountPercent: number, hasDiscount: boolean, period: string, isYearly: boolean }}
   */
  const priceInfo = useMemo(() => {
    const discPct = parseFloat(subscription?.discountPercent) || 0;
    const isYearly = subscription?.paymentCycle?.toUpperCase() === "YEARLY";
    const basePrice = parseFloat(
      subscription?.originalAmount ||
        (isYearly ? servicePlan?.priceYearly : servicePlan?.priceMonthly) ||
        0,
    );
    // subscription.amount is already the post-discount price stored by the
    // backend, so we must NOT apply the discount again.
    const displayPrice = parseFloat(
      subscription?.amount ||
        (isYearly ? servicePlan?.priceYearly : servicePlan?.priceMonthly) ||
        0,
    );

    return {
      basePrice,
      displayPrice,
      discountPercent: discPct,
      hasDiscount: discPct > 0 && basePrice > 0,
      period: isYearly ? "year" : "month",
      isYearly,
    };
  }, [subscription, servicePlan]);

  /**
   * Gets subscription status details
   * @returns {Object} Subscription status information
   */
  const getSubscriptionStatus = () => {
    if (!subscription) {
      return {
        isActive: false,
        status: null,
        daysRemaining: null,
        isExpiringSoon: false,
        isTrial: false,
      };
    }

    const endDate = subscription.endDate
      ? new Date(subscription.endDate)
      : null;
    const today = new Date();
    const daysRemaining = endDate
      ? Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))
      : null;

    const statusLower = subscription.status?.toLowerCase();

    return {
      isActive: statusLower === "active",
      isInGracePeriod: statusLower === "grace_period",
      status: subscription.status,
      daysRemaining,
      isExpiringSoon:
        daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0,
      isTrial: subscription.isTrial || false,
      startDate: subscription.startDate,
      endDate: subscription.endDate,
      autoRenew: subscription.autoRenew || false,
      graceEndDate: subscription.graceEndDate || null,
      paymentMethodOnFile: subscription.paymentMethodOnFile || false,
    };
  };

  /**
   * Gets payment information
   * @returns {Object} Payment details
   */
  const getPaymentInfo = () => {
    if (!subscription || !servicePlan) {
      return {
        amount: 0,
        cycle: null,
        currency: "USD",
      };
    }

    return {
      amount: servicePlan.price || 0,
      cycle: subscription.paymentCycle || servicePlan.billingCycle,
      currency: "USD",
      nextBillingDate: subscription.endDate,
    };
  };

  // =========================================================================
  // EVENT LISTENERS
  // =========================================================================

  /**
   * Keep a stable ref to refreshSubscription so the event listener
   * always calls the latest version (avoids stale closure from [] deps).
   */
  const refreshRef = React.useRef(refreshSubscription);
  React.useEffect(() => {
    refreshRef.current = refreshSubscription;
  });

  /**
   * Listen for subscription update events
   * Automatically refresh subscription data when changes occur
   */
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      console.log("📢 SubscriptionContext: Subscription update event received");
      refreshRef.current();
    };

    window.addEventListener("subscriptionUpdated", handleSubscriptionUpdate);

    return () => {
      window.removeEventListener(
        "subscriptionUpdated",
        handleSubscriptionUpdate,
      );
    };
  }, []);

  /**
   * Auto-load subscription when userUuid changes
   */
  useEffect(() => {
    if (userUuid) {
      console.log(
        "👤 SubscriptionContext: User UUID changed, loading subscription...",
      );
      fetchSubscriptionData();
    } else {
      console.log(
        "👤 SubscriptionContext: No user UUID, clearing subscription...",
      );
      clearSubscription();
    }
  }, [userUuid]);

  // =========================================================================
  // CONTEXT VALUE
  // =========================================================================

  const value = {
    // State
    subscription,
    servicePlan,
    hasActiveSubscription,
    hasLapsedSubscription,
    isInGracePeriod: subscription?.status?.toLowerCase() === "grace_period",
    isLoading,
    hasInitiallyLoaded,
    error,

    // Core functions
    fetchSubscriptionData,
    refreshSubscription,
    clearSubscription,

    // Helper functions
    getMaxUsers,
    getPlanName,
    getPlanId,
    hasFeature,
    canShare,
    canAddUsers,
    canDowngradeTo,
    getSubscriptionStatus,
    getPaymentInfo,
    getDiscountedPrice,

    // Pre-computed pricing
    priceInfo,
  };

  console.log("🎯 SubscriptionContext: Providing context value:", {
    hasActiveSubscription,
    planName: getPlanName(),
    maxUsers: getMaxUsers(),
    isLoading,
  });

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
